import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { api } from "api";
import { T_Api_Success_Res } from "./api.types";
import {
  PanelStatus,
  PolicySeverity,
  SmartNegotiationPanelData,
  T_CustomOfferRequest,
  T_CustomOfferResult,
  T_GenerateOffersResponse,
  T_NegotiationBaseRequest,
  T_NegotiationContext,
  T_NegotiationContextRequest,
  T_Policy,
  T_SavedCustomOffer,
} from "dtos/smart-negotiation.dto";
import {
  T_OfferAssistantChatRequest,
  T_OfferAssistantChatResponse,
  T_OfferAssistantHistoryResponse,
} from "dtos/offer-assistant.dto";

export type T_NegotiationObjective = "settlement" | "restructure";

export type T_NegotiateAs = "client" | "fcds";

export type T_FlagSeverity = "deadline" | "financial" | "followup" | "legal";

export type T_TagVariant = "danger" | "info" | "neutral";

export type T_BulletListVariant = "positive" | "warning";

export type T_SmartPanelBaseRequest = {
  source_uri?: string;

  max_settlement_amount?: number;

  max_weekly_amount?: number;

  total_weeks: number;

  hardship_flags?: string[];

  objective: T_NegotiationObjective;

  negotiate_as: T_NegotiateAs;

  creditor_email?: string | null;

  creditor_allowed_emails?: string[];

  is_partner?: boolean;
};

export type T_SmartPanelFirstOutboundRequest = T_SmartPanelBaseRequest;

export type T_RawNegotiationHistory = {
  previous_creditor_responses?: string[];

  previous_email_responses?: string[];
};

export type T_SmartPanelFollowupRequest = T_SmartPanelBaseRequest & {
  negotiation_history: T_RawNegotiationHistory;

  creditor_response: string;
};

export type T_FirstOutboundParams = {
  appkey?: string;
  loan_id?: string;
};

export type T_FollowupParams = {
  appkey: string;
  loan_id: string;
  thread_id: string;
  message_id: string;
};

export type T_SmartPanelParams = {
  appkey: string;
  loan_id: string;

  thread_id?: string;

  message_id?: string;
};

export type T_CounterCard = {
  id: string;
  type: "counter";
  title: string;

  theirAmount?: number;
  ourAmount?: number;

  currency: string;
};

export type T_FlagItem = {
  id: string;

  severity: T_FlagSeverity;

  title: string;

  description: string;
};

export type T_FlagsCard = {
  id: string;
  type: "flags";

  flags: T_FlagItem[];
};

export type T_TagItem = {
  label: string;
  variant: T_TagVariant;
};

export type T_CaseSummaryCard = {
  id: string;
  type: "case_summary";

  sectionLabel: string;

  entityName: string;

  fileNumber: string;

  funderLabel: string;

  description: string;

  tags: T_TagItem[];
};

export type T_MoneyCapacityCard = {
  id: string;
  type: "money_capacity";

  sectionLabel: string;

  rows: Record<string, string>;

  note?: string;
};

export type T_BulletListCard = {
  id: string;
  type: "bullet_list";

  title: "Angles worth considering" | "Often backfires here" | "Consider saying" | "Don't say";

  variant: T_BulletListVariant;

  items: string[];
};

export type T_SmartNegotiationPanelCard =
  | T_CounterCard
  | T_FlagsCard
  | T_CaseSummaryCard
  | T_MoneyCapacityCard
  | T_BulletListCard;

export type T_SmartNegotiationPanelData = {
  /**
   * Always "Smart negotiations".
   */
  title: string;

  /**
   * Fixed advisory disclaimer.
   */
  subtitle: string;

  /**
   * Discriminated union.
   */
  cards: T_SmartNegotiationPanelCard[];
};

export type AuditRule = {
  key: string;
  name: string;
  severity: PolicySeverity;
  hint?: string;
};

export type AuditResult = {
  org_code: string;
  valid_rules: AuditRule[];
  invalid_rules: AuditRule[];
};
export type FixResult = {
  subject: string;
  body: string;
};

export const negotiationService = {
  useFirstOutbound: () => {
    return useMutation({
      mutationKey: ["negotiationv2", "smart-panel", "first-outbound"],

      mutationFn: async ({
        body,
      }: {
        body: {
          appkey?: string | undefined;
          loan_id?: string | undefined;
        };
      }) => {
        const res = await api.post("/smart-negotiation/first-outbound", body);

        return res.data as T_Api_Success_Res<T_SmartNegotiationPanelData>;
      },
    });
  },

  useGetSmartPanel: ({
    application_id,
    loan_id,
    thread_id,
    message_id,
  }: {
    application_id?: string;
    loan_id?: string;
    thread_id?: string;
    message_id?: string;
  }) => {
    return useQuery({
      queryKey: ["negotiationv2", "smart-panel", "get-smart-panel", application_id, loan_id, thread_id, message_id],

      queryFn: async () => {
        const res = await api.get(`/smart-negotiation/smart-panel`, {
          params: {
            application_id,
            loan_id,
            thread_id,
            message_id,
          },
        });

        return res.data as T_Api_Success_Res<SmartNegotiationPanelData>;
      },
      refetchInterval: (query) => {
        const status = query.state.data?.result?.status;
        if (status === PanelStatus.COMPLETED || status === PanelStatus.ERROR || status === PanelStatus.STALLED) {
          return false; // stop polling on terminal states
        }
        return 5000;
      },

      enabled: !!application_id && !!loan_id,
    });
  },
  useFollowup: () => {
    return useMutation({
      mutationKey: ["negotiationv2", "followup"],

      mutationFn: async (body: {
        application_id?: string;
        loan_id?: string;
        thread_id?: string;
        message_id?: string;
        orgCode?: string;
      }) => {
        const res = await api.post("/smart-negotiation/followup", body);

        return res.data;
      },
    });
  },
  usePolicies() {
    return useQuery({
      queryKey: ["policies"],
      queryFn: async () => {
        const res = await api.get(`/smart-negotiation/v2/negotiation/audit/policies`);
        return res.data as T_Api_Success_Res<T_Policy[]>;
      },
    });
  },
  useOrgPolicies(org_code: string | undefined) {
    return useQuery({
      queryKey: ["policies", "org-policies"],
      queryFn: async () => {
        const res = await api.get(`/smart-negotiation/v2/negotiation/audit/policies/org-mapping`, {
          params: { org_code },
        });
        return res.data as T_Api_Success_Res<T_Policy[]>;
      },
      enabled: !!org_code,
    });
  },
  useSavePolicies() {
    return useMutation({
      mutationKey: ["policies", "save-policies"],
      mutationFn: async ({ body }: { body: { org_code: string; rule_keys: string[] } }) => {
        const res = await api.post(`/smart-negotiation/v2/negotiation/audit/policies/org-mapping`, body);
        return res.data;
      },
    });
  },
  useAuditOutbound: () => {
    return useMutation({
      mutationKey: ["audit", "audit-outbound"],

      mutationFn: async ({
        body,
      }: {
        body: {
          org_code: string;
          message_id: string;
          email: {
            subject: string;
            body: string;
          };
        };
      }) => {
        const res = await api.post("/smart-negotiation/v2/negotiation/audit/audit-outbound", body);

        return res.data as T_Api_Success_Res<AuditResult>;
      },
    });
  },

  useApplyFix: () => {
    return useMutation({
      mutationKey: ["audit", "fix", "apply-changes"],

      mutationFn: async ({
        body,
      }: {
        body: {
          message_id: string;
          selected_fixes: { key: string }[];
        };
      }) => {
        const res = await api.post("/smart-negotiation/v2/negotiation/audit/fix/apply-changes", body);

        return res.data as T_Api_Success_Res<FixResult>;
      },
    });
  },

  useGetAuditResults: (message_id?: string) => {
    return useQuery({
      queryKey: ["audit", "fix", "get-audit-result", message_id],

      queryFn: async () => {
        const res = await api.get("/smart-negotiation/v2/negotiation/audit/audit-outbound", {
          params: {
            message_id,
          },
        });

        return res.data as T_Api_Success_Res<AuditResult>;
      },
      enabled: !!message_id,
    });
  },
  useGetAppliedFix: (message_id?: string) => {
    return useQuery({
      queryKey: ["audit", "fix", "get-applied-changes", message_id],

      queryFn: async () => {
        const res = await api.get("/smart-negotiation/v2/negotiation/audit/fix/apply-changes", {
          params: {
            message_id,
          },
        });

        return res.data as T_Api_Success_Res<FixResult>;
      },
      enabled: !!message_id,
    });
  },
  useGetContext: ({ application_id, loan_id }: { application_id?: string; loan_id?: string }) => {
    return useQuery({
      queryKey: ["negotiation-offer", "context", application_id, loan_id],

      queryFn: async () => {
        const res = await api.get(`/smart-negotiation/context/${application_id}/${loan_id}`);

        return res.data as T_Api_Success_Res<T_NegotiationContext>;
      },

      enabled: !!application_id && !!loan_id,
      retry: false,
    });
  },

  useUpsertContext: () => {
    return useMutation({
      mutationKey: ["negotiation-offer", "context", "upsert"],

      mutationFn: async ({ body }: { body: T_NegotiationContextRequest }) => {
        const res = await api.post(`/smart-negotiation/context`, body);

        return res.data as T_Api_Success_Res<T_NegotiationContext>;
      },
    });
  },

  useGenerateOffers: () => {
    return useMutation({
      mutationKey: ["negotiation-offer", "offers", "generate"],

      mutationFn: async ({ body }: { body: T_NegotiationBaseRequest }) => {
        const res = await api.post(`/smart-negotiation/generate-offer`, body);

        return res.data as T_Api_Success_Res<T_GenerateOffersResponse>;
      },
    });
  },

  usePreviewCustomOffer: () => {
    return useMutation({
      mutationKey: ["negotiation-offer", "offers", "preview-custom"],

      mutationFn: async ({ body }: { body: T_CustomOfferRequest }) => {
        const res = await api.post(`/smart-negotiation/preview-custom-request`, body);

        return res.data as T_Api_Success_Res<T_CustomOfferResult>;
      },
    });
  },

  useSaveCustomOffer: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: ["negotiation-offer", "offers", "save-custom"],

      mutationFn: async ({ body }: { body: T_CustomOfferRequest }) => {
        const res = await api.post(`/smart-negotiation/save-custom-offer`, body);

        return res.data as T_Api_Success_Res<T_SavedCustomOffer>;
      },

      onSuccess: (_data, variables) => {
        // Refresh the saved-offers list so the sidebar picks up the new entry.
        queryClient.invalidateQueries({
          queryKey: [
            "negotiation-offer",
            "offers",
            "saved-custom",
            variables.body.application_id,
            variables.body.loan_id,
          ],
        });
      },
    });
  },

  useSavedCustomOffers: ({
    application_id,
    loan_id,
    enabled = true,
    message_id,
    thread_id,
  }: {
    application_id?: string;
    loan_id?: string;
    enabled?: boolean;
    thread_id?: string;
    message_id?: string;
  }) => {
    return useQuery({
      queryKey: ["negotiation-offer", "offers", "saved-custom", application_id, loan_id, thread_id, message_id],

      queryFn: async () => {
        let url = `/smart-negotiation/custom-offer/${application_id}/${loan_id}`;
        const queryParams = new URLSearchParams();
        if (thread_id) {
          queryParams.append("thread_id", thread_id);
        }
        if (message_id) {
          queryParams.append("message_id", message_id);
        }
        const queryString = queryParams.toString();
        if (queryString) {
          url += `?${queryString}`;
        }
        const res = await api.get(url);

        return res.data as T_Api_Success_Res<T_SavedCustomOffer[]>;
      },

      enabled: enabled && !!application_id && !!loan_id,
    });
  },

  useLatestSavedCustomOffer: ({ appkey, loan_id }: { appkey?: string; loan_id?: string }) => {
    return useQuery({
      queryKey: ["negotiation-offer", "offers", "saved-custom-latest", appkey, loan_id],

      queryFn: async () => {
        const res = await api.get(`/smart-negotiation/offers/saved-custom-latest`, {
          params: { appkey, loan_id },
        });

        return res.data as T_Api_Success_Res<T_SavedCustomOffer>;
      },

      enabled: !!appkey && !!loan_id,
    });
  },

  useOfferAssistantHistory: ({
    application_id,
    loan_id,
    thread_id,
    enabled = true,
  }: {
    application_id?: string;
    loan_id?: string;
    thread_id?: string;
    enabled?: boolean;
  }) => {
    return useQuery({
      queryKey: ["negotiation-offer", "assistant", "chat-history", application_id, loan_id, thread_id],

      queryFn: async () => {
        try {
          const res = await api.get(`/smart-negotiation/offer-assistant/${application_id}/${loan_id}/chat/history`, {
            params: { thread_id },
          });

          return res.data as T_Api_Success_Res<T_OfferAssistantHistoryResponse>;
        } catch (error) {
          // 404 = no chat exists yet for this loan/thread — the drawer bootstraps a new one.
          if (isAxiosError(error) && error.response?.status === 404) return null;
          throw error;
        }
      },

      enabled: enabled && !!application_id && !!loan_id,
      retry: false,
      refetchOnWindowFocus: false,
    });
  },

  useSendOfferAssistantMessage: () => {
    return useMutation({
      mutationKey: ["negotiation-offer", "assistant", "chat"],

      mutationFn: async ({ body }: { body: T_OfferAssistantChatRequest }) => {
        const res = await api.post(`/smart-negotiation/offer-assistant/chat`, body);

        return res.data as T_Api_Success_Res<T_OfferAssistantChatResponse>;
      },
    });
  },
};
